import React from 'react';

import { Helmet } from 'react-helmet-async';

/**
 * 공개(색인 대상) 페이지의 SEO 메타를 한 곳에서 관리하는 컴포넌트.
 *
 * 페이지별 title/description/canonical/OG/Twitter/JSON-LD를 <head>에 주입한다.
 * 빌드 타임 프리렌더(scripts/prerender.js)가 helmet이 주입한 head까지 스냅샷에
 * 굳혀주므로, JS를 실행하지 않는 크롤러(예: 네이버 Yeti)도 이 값을 읽을 수 있다.
 *
 * 주의: index.html에는 정적 SEO 태그를 두지 않는다(helmet은 기존 정적 태그를 지우지
 * 않아 프리렌더 결과에 중복 태그가 생기기 때문). SEO 메타의 단일 출처는 이 컴포넌트다.
 */
export const SITE_URL = 'https://admin.checkmatekorea.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/GuideLogo.png`;

export interface SeoProps {
  /** 브라우저 탭·검색결과 제목 */
  title: string;
  /** 검색결과 스니펫에 쓰이는 요약(권장 70~160자) */
  description: string;
  /** canonical 경로. 예: '/guide' (기본값 '/') */
  path?: string;
  /** 공유 카드 이미지 절대 URL (기본값: 브랜드 로고) */
  ogImage?: string;
  /** 색인 제외가 필요한 경우 true */
  noindex?: boolean;
  /** JSON-LD 구조화 데이터(Organization 등) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
}) => {
  const url = `${SITE_URL}${path}`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="체크메이트코리아" />
      <meta property="og:locale" content="ko_KR" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default Seo;
